export type EulaBlock =
  | { type: 'heading'; level: 2 | 3 | 4; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] };

export const EULA_SECTIONS: EulaBlock[] = [
  // ── Preamble ──
  { type: 'paragraph', text: 'Playcast Desktop Game Streaming Software' },
  {
    type: 'paragraph',
    text: 'IMPORTANT - READ CAREFULLY: This End User License Agreement ("Agreement" or "EULA") is a legal agreement between you (either an individual or a single entity) ("You" or "User") and Playcast Inc. ("Company," "we," "us," or "our") for the Playcast desktop streaming software application, including any associated drivers, updates, and documentation (collectively, the "Software").',
  },
  {
    type: 'paragraph',
    text: 'BY INSTALLING, COPYING, OR OTHERWISE USING THE SOFTWARE, YOU AGREE TO BE BOUND BY THE TERMS OF THIS AGREEMENT. IF YOU DO NOT AGREE TO THE TERMS OF THIS AGREEMENT, DO NOT INSTALL OR USE THE SOFTWARE.',
  },
  {
    type: 'paragraph',
    text: 'IMPORTANT: Your use of Playcast\'s online Service is governed by our separate Terms of Service, which can be found at www.playcast.io. This EULA governs only the downloadable Software components.',
  },

  // ── 1. License Grant and Eligibility ──
  { type: 'heading', level: 2, text: '1. License Grant and Eligibility' },
  { type: 'heading', level: 3, text: '1.1 Age Requirement' },
  {
    type: 'paragraph',
    text: 'You must be at least 18 years old to use the Software. By agreeing to this EULA, you represent and warrant that you are at least 18 years old and have not previously been suspended or removed from any Playcast services.',
  },
  { type: 'heading', level: 3, text: '1.2 Limited License' },
  {
    type: 'paragraph',
    text: 'Subject to your compliance with this Agreement, Company grants you a limited, non-exclusive, non-transferable, revocable license to install and use the Software on devices you own or control solely for your personal, non-commercial use.',
  },
  { type: 'heading', level: 3, text: '1.3 Driver Installation' },
  {
    type: 'paragraph',
    text: 'You acknowledge and agree that the Software requires the installation of system-level drivers and components to enable game and application streaming functionality. You expressly consent to the installation, operation, and automatic updating of these drivers.',
  },
  { type: 'heading', level: 3, text: '1.4 Restrictions' },
  { type: 'paragraph', text: 'You may not:' },
  {
    type: 'list',
    items: [
      'Distribute, sublicense, rent, lease, or lend the Software',
      'Reverse engineer, decompile, or disassemble the Software',
      'Remove or modify any proprietary notices or labels',
      'Use the Software for commercial purposes without explicit written permission',
      'Circumvent or disable any security features or usage restrictions',
      'Use the Software in violation of our Terms of Service or for any illegal purpose',
    ],
  },

  // ── 2. System Requirements and Compatibility ──
  { type: 'heading', level: 2, text: '2. System Requirements and Compatibility' },
  { type: 'heading', level: 3, text: '2.1 System Modifications' },
  {
    type: 'paragraph',
    text: 'You acknowledge that the Software makes significant modifications to your computer system, including but not limited to:',
  },
  {
    type: 'list',
    items: [
      'Installation of kernel-level drivers',
      'Network configuration changes',
      'Graphics and audio system modifications',
      'Registry and system file modifications',
    ],
  },
  { type: 'heading', level: 3, text: '2.2 Compatibility Disclaimer' },
  {
    type: 'paragraph',
    text: 'Company does not guarantee compatibility with all hardware configurations, operating systems, or third-party software. You are responsible for ensuring your system meets minimum requirements.',
  },

  // ── 3. Streaming and Content Responsibilities ──
  { type: 'heading', level: 2, text: '3. Streaming and Content Responsibilities' },
  { type: 'heading', level: 3, text: '3.1 Content Compliance' },
  {
    type: 'paragraph',
    text: 'You are solely responsible for ensuring that any games, applications, or content you stream through the Software comply with:',
  },
  {
    type: 'list',
    items: [
      'All applicable third-party End User License Agreements',
      'Copyright and intellectual property laws',
      'Platform-specific terms of service',
      'Local, state, and federal regulations',
    ],
  },
  { type: 'heading', level: 3, text: '3.2 Third-Party EULA Compliance' },
  {
    type: 'paragraph',
    text: 'You acknowledge that streaming games or applications may violate their respective EULAs. You agree to review and comply with all applicable third-party agreements before streaming any content.',
  },
  { type: 'heading', level: 3, text: '3.3 Prohibited Content' },
  { type: 'paragraph', text: 'You may not use the Software to stream:' },
  {
    type: 'list',
    items: [
      'Illegal, harmful, or offensive content',
      'Content that violates third-party intellectual property rights',
      'Content that violates any applicable laws or regulations',
    ],
  },

  // ── 4. Data Collection and Privacy ──
  { type: 'heading', level: 2, text: '4. Data Collection and Privacy' },
  { type: 'heading', level: 3, text: '4.1 Data Collection' },
  {
    type: 'paragraph',
    text: 'You acknowledge and consent to Company\'s collection, processing, and storage of:',
  },
  {
    type: 'list',
    items: [
      'System performance and hardware information',
      'Usage statistics and streaming metrics',
      'Error logs and diagnostic information',
      'Network and connection data',
      'Game and application usage patterns',
      'Information described in our Privacy Policy',
    ],
  },
  { type: 'heading', level: 3, text: '4.2 Analytics and Improvement' },
  { type: 'paragraph', text: 'Company may use collected data to:' },
  {
    type: 'list',
    items: [
      'Improve Software performance and features',
      'Provide technical support',
      'Develop new features and services',
      'Generate anonymized analytics and reports',
      'Provide customized content and functionality',
    ],
  },
  { type: 'heading', level: 3, text: '4.3 Privacy Policy' },
  {
    type: 'paragraph',
    text: 'Your use of the Software is also governed by our Privacy Policy, which can be found at www.playcast.io and is incorporated herein by reference. The Privacy Policy explains how we collect, use, and protect your information, including automatic data collection and sharing with third-party services.',
  },
  { type: 'heading', level: 3, text: '4.4 International Data Transfers' },
  {
    type: 'paragraph',
    text: 'You acknowledge that information collected by the Software may be stored and processed in the United States or other countries where Company or its service providers maintain facilities.',
  },

  // ── 5. Prohibited Conduct ──
  { type: 'heading', level: 2, text: '5. Prohibited Conduct' },
  {
    type: 'paragraph',
    text: 'In addition to the restrictions in Section 1.4, you agree not to use the Software in any manner that violates the Prohibited Conduct provisions of our Terms of Service. This includes but is not limited to using the Software for illegal purposes, harassing other users, violating intellectual property rights, or interfering with the operation of our services. The complete list of prohibited conduct can be found in our Terms of Service at www.playcast.io.',
  },

  // ── 6. Security and System Risks ──
  { type: 'heading', level: 2, text: '6. Security and System Risks' },
  { type: 'heading', level: 3, text: '6.1 Security Acknowledgment' },
  { type: 'paragraph', text: 'You acknowledge that the Software:' },
  {
    type: 'list',
    items: [
      'Creates network connections that may expose your system to security risks',
      'Installs system-level components that may affect system security',
      'Enables remote access to your games and applications',
      'May introduce vulnerabilities inherent in streaming technology',
    ],
  },
  { type: 'heading', level: 3, text: '6.2 Third-Party Computer Usage' },
  {
    type: 'paragraph',
    text: 'IMPORTANT DISCLAIMER: When using Playcast\'s online services in conjunction with this Software, you understand and acknowledge that you may be playing games hosted on other users\' personal computers. You expressly understand and agree that:',
  },
  {
    type: 'list',
    items: [
      'Playcast does not own, control, or monitor these third-party computers',
      'Playcast cannot guarantee the security, privacy, or integrity of third-party systems',
      'You assume all risks associated with accessing games on other users\' computers',
      'Other users\' computers may have different security configurations and vulnerabilities',
      'Playcast disclaims all responsibility for any security breaches, data loss, or other issues arising from use of third-party computers',
    ],
  },
  { type: 'heading', level: 3, text: '6.3 Security Responsibility' },
  { type: 'paragraph', text: 'You are solely responsible for:' },
  {
    type: 'list',
    items: [
      'Maintaining appropriate security measures on your system',
      'Monitoring and controlling access to your streaming sessions',
      'Protecting sensitive information during streaming',
      'Regular system updates and security patches',
      'Understanding the risks of distributed gaming platforms',
    ],
  },

  // ── 7. Limitation of Liability and Indemnification ──
  { type: 'heading', level: 2, text: '7. Limitation of Liability and Indemnification' },
  { type: 'heading', level: 3, text: '7.1 Disclaimer of Warranties' },
  {
    type: 'paragraph',
    text: 'THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
  },
  { type: 'heading', level: 3, text: '7.2 Limitation of Liability' },
  {
    type: 'paragraph',
    text: 'IN NO EVENT SHALL COMPANY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:',
  },
  {
    type: 'list',
    items: [
      'System damage or data loss',
      'Security breaches or unauthorized access',
      'Third-party claims related to streamed content',
      'Business interruption or lost profits',
      'Hardware damage or malfunction',
    ],
  },
  {
    type: 'paragraph',
    text: 'COMPANY\'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE SOFTWARE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.',
  },
  { type: 'heading', level: 3, text: '7.3 User Indemnification' },
  {
    type: 'paragraph',
    text: 'You agree to indemnify, defend, and hold harmless Company from and against any claims, damages, losses, or expenses arising from:',
  },
  {
    type: 'list',
    items: [
      'Your use of the Software',
      'Your violation of this Agreement or our Terms of Service',
      'Your violation of third-party rights or agreements',
      'Content you stream through the Software',
      'Any system damage or security issues resulting from Software use',
    ],
  },

  // ── 8. System Impact and User Acknowledgments ──
  { type: 'heading', level: 2, text: '8. System Impact and User Acknowledgments' },
  { type: 'heading', level: 3, text: '8.1 Performance Impact' },
  { type: 'paragraph', text: 'You acknowledge that the Software may:' },
  {
    type: 'list',
    items: [
      'Affect system performance and resource usage',
      'Interfere with other applications or drivers',
      'Require significant network bandwidth',
      'Impact battery life on mobile devices',
    ],
  },
  { type: 'heading', level: 3, text: '8.2 System Changes' },
  {
    type: 'paragraph',
    text: 'You acknowledge and accept responsibility for any system changes, including:',
  },
  {
    type: 'list',
    items: [
      'Driver conflicts or compatibility issues',
      'System instability or crashes',
      'Network configuration changes',
      'Registry or system file modifications',
    ],
  },

  // ── 9. Automatic Updates ──
  { type: 'heading', level: 2, text: '9. Automatic Updates' },
  { type: 'heading', level: 3, text: '9.1 Update Consent' },
  {
    type: 'paragraph',
    text: 'You agree that the Software may automatically download and install updates, including driver updates, without prior notice. These updates are subject to this Agreement.',
  },
  { type: 'heading', level: 3, text: '9.2 Update Requirements' },
  {
    type: 'paragraph',
    text: 'Some updates may be required for continued use of the Software. Failure to install required updates may result in loss of functionality.',
  },

  // ── 10. Termination ──
  { type: 'heading', level: 2, text: '10. Termination' },
  { type: 'heading', level: 3, text: '10.1 Termination Rights' },
  {
    type: 'paragraph',
    text: 'This Agreement is effective until terminated. Company may terminate this Agreement immediately if you violate any terms or if your access to Playcast services is terminated under our Terms of Service. You may terminate by uninstalling the Software.',
  },
  { type: 'heading', level: 3, text: '10.2 Effect of Termination' },
  { type: 'paragraph', text: 'Upon termination:' },
  {
    type: 'list',
    items: [
      'Your license rights immediately cease',
      'You must cease all use of the Software',
      'Certain data collection practices may continue as outlined in the Privacy Policy',
      'Installed drivers may remain unless manually removed',
    ],
  },

  // ── 11. Third-Party Services and Integrations ──
  { type: 'heading', level: 2, text: '11. Third-Party Services and Integrations' },
  { type: 'heading', level: 3, text: '11.1 Third-Party Dependencies' },
  {
    type: 'paragraph',
    text: 'The Software may integrate with or depend on third-party services, platforms, or APIs. Company is not responsible for the availability, functionality, or terms of such third-party services.',
  },
  { type: 'heading', level: 3, text: '11.2 Platform Compliance' },
  {
    type: 'paragraph',
    text: 'You are responsible for ensuring your use of the Software complies with the terms of service of any gaming platforms, digital distribution services, or other third-party services you access through the Software.',
  },

  // ── 12. Export Compliance ──
  { type: 'heading', level: 2, text: '12. Export Compliance' },
  {
    type: 'paragraph',
    text: 'You acknowledge that the Software may be subject to export control laws and regulations. You agree to comply with all applicable export and import laws and regulations.',
  },

  // ── 13. Dispute Resolution ──
  { type: 'heading', level: 2, text: '13. Dispute Resolution' },
  { type: 'heading', level: 3, text: '13.1 Arbitration' },
  {
    type: 'paragraph',
    text: 'Any disputes arising from this Agreement are subject to the dispute resolution and arbitration provisions set forth in our Terms of Service, including the binding arbitration requirement and class action waiver. The complete arbitration procedures can be found at www.playcast.io.',
  },
  { type: 'heading', level: 3, text: '13.2 Governing Law' },
  {
    type: 'paragraph',
    text: 'This Agreement shall be governed by the laws of the State of Delaware, without regard to conflict of law principles, consistent with our Terms of Service.',
  },

  // ── 14. General Provisions ──
  { type: 'heading', level: 2, text: '14. General Provisions' },
  { type: 'heading', level: 3, text: '14.1 Entire Agreement' },
  {
    type: 'paragraph',
    text: 'This Agreement, together with our Terms of Service and Privacy Policy, constitutes the entire agreement between the parties regarding the Software.',
  },
  { type: 'heading', level: 3, text: '14.2 Relationship to Terms of Service' },
  {
    type: 'paragraph',
    text: 'This EULA supplements our Terms of Service. In case of conflicts between this EULA and the Terms of Service regarding the Software specifically, this EULA controls. For all other matters, the Terms of Service controls.',
  },
  { type: 'heading', level: 3, text: '14.3 Severability' },
  {
    type: 'paragraph',
    text: 'If any provision of this Agreement is found to be unenforceable, the remainder shall remain in full force and effect.',
  },
  { type: 'heading', level: 3, text: '14.4 No Waiver' },
  {
    type: 'paragraph',
    text: 'Company\'s failure to enforce any provision shall not constitute a waiver of such provision or any other provision.',
  },
  { type: 'heading', level: 3, text: '14.5 Assignment' },
  {
    type: 'paragraph',
    text: 'You may not assign this Agreement without Company\'s prior written consent. Company may assign this Agreement without restriction.',
  },

  // ── 15. Contact Information ──
  { type: 'heading', level: 2, text: '15. Contact Information' },
  { type: 'paragraph', text: 'For questions regarding this Agreement, please contact:' },
  {
    type: 'paragraph',
    text: 'Playcast Inc.\n1000 SE Everett Mall Way #40p\nEverett, WA 98208\nEmail: support@playcast.io\nPrivacy-related inquiries: privacy@playcast.io',
  },

  // ── 16. Acknowledgment ──
  { type: 'heading', level: 2, text: '16. Acknowledgment' },
  {
    type: 'paragraph',
    text: 'BY CLICKING "I AGREE" OR BY INSTALLING OR USING THE SOFTWARE, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS AGREEMENT, UNDERSTAND IT, AND AGREE TO BE BOUND BY ITS TERMS AND CONDITIONS.',
  },

  // ═══════════════════════════════════════════════════
  // PLAYCAST PAVILION™ SOFTWARE ADDENDUM
  // ═══════════════════════════════════════════════════
  { type: 'heading', level: 2, text: 'PLAYCAST PAVILION™ SOFTWARE ADDENDUM' },
  { type: 'heading', level: 3, text: 'End User License Agreement Addendum' },
  {
    type: 'paragraph',
    text: 'IMPORTANT: This Addendum supplements and modifies the main End User License Agreement ("Main EULA") for users who elect to enable Playcast Pavilion™ software functionality. By enabling Playcast Pavilion™ features, you agree to the additional terms below in addition to all terms in the Main EULA and our Terms of Service.',
  },
  {
    type: 'paragraph',
    text: 'This Addendum is part of the software license agreement with Playcast Inc. Use of Playcast Pavilion™ also requires compliance with our Terms of Service at www.playcast.io.',
  },
  {
    type: 'paragraph',
    text: 'ELIGIBILITY: You must be at least 18 years old to use Playcast Pavilion™ features, consistent with our Terms of Service requirements.',
  },

  // ── Addendum 1. Service Overview ──
  { type: 'heading', level: 2, text: '1. Playcast Pavilion™ Service Overview' },
  { type: 'heading', level: 3, text: '1.1 Service Description' },
  {
    type: 'paragraph',
    text: 'Playcast Pavilion™ is a distributed gaming platform that utilizes your computer\'s idle resources to host gaming sessions for other users in our network. When you enable Playcast Pavilion™, your computer becomes part of our distributed hosting infrastructure.',
  },
  { type: 'heading', level: 3, text: '1.2 Critical Understanding - Distributed Gaming Risks' },
  {
    type: 'paragraph',
    text: 'YOU EXPRESSLY ACKNOWLEDGE AND UNDERSTAND that Playcast Pavilion™ enables you to:',
  },
  {
    type: 'list',
    items: [
      'Play games hosted on other users\' personal computers that Playcast does not own, control, or monitor',
      'Host games on your computer for other users to access remotely',
      'Share computing resources in a peer-to-peer gaming network',
    ],
  },
  {
    type: 'paragraph',
    text: 'YOU ASSUME ALL RISKS associated with this distributed model, including but not limited to security vulnerabilities, data exposure, system instability, and privacy concerns that may arise from playing games on third-party computers or hosting games for others.',
  },
  { type: 'heading', level: 3, text: '1.3 Voluntary Participation' },
  {
    type: 'paragraph',
    text: 'Playcast Pavilion™ functionality is entirely optional. You may choose to enable or disable Playcast Pavilion™ features, but access to hosted games through our service requires maintaining active Playcast Pavilion™ participation.',
  },
  { type: 'heading', level: 3, text: '1.4 Resource Sharing Agreement' },
  {
    type: 'paragraph',
    text: 'By enabling Playcast Pavilion™, you agree to share your computer\'s idle processing power, network bandwidth, and storage capacity to host gaming sessions for other users in exchange for access to games hosted on other users\' systems.',
  },

  // ── Addendum 2. System Access and Modifications ──
  { type: 'heading', level: 2, text: '2. System Access and Modifications' },
  { type: 'heading', level: 3, text: '2.1 Account Creation Authority' },
  { type: 'paragraph', text: 'You grant Company explicit permission to:' },
  {
    type: 'list',
    items: [
      'Create and manage user accounts on your local system',
      'Configure account permissions and access rights',
      'Install and configure user profiles for gaming services',
      'Modify account settings as necessary for service operation',
    ],
  },
  { type: 'heading', level: 3, text: '2.2 Game Service Installation' },
  { type: 'paragraph', text: 'Company is authorized to:' },
  {
    type: 'list',
    items: [
      'Install game clients, launchers, and related software in created accounts',
      'Download, install, and update games and game services',
      'Configure gaming platforms and digital distribution clients',
      'Manage game libraries and installations across accounts',
    ],
  },
  { type: 'heading', level: 3, text: '2.3 System Resource Usage' },
  {
    type: 'paragraph',
    text: 'You acknowledge and consent to Company\'s use of:',
  },
  {
    type: 'list',
    items: [
      'CPU processing power during idle periods',
      'Available RAM and system memory',
      'Network bandwidth for gaming sessions and data transfer',
      'Storage space for game installations and temporary files',
      'Graphics processing capabilities for game rendering',
    ],
  },

  // ── Addendum 3. Network Access and VPN Requirements ──
  { type: 'heading', level: 2, text: '3. Network Access and VPN Requirements' },
  { type: 'heading', level: 3, text: '3.1 VPN Connection Mandate' },
  {
    type: 'paragraph',
    text: 'All Playcast Pavilion™ operations require connection to Company\'s private VPN network. You agree that:',
  },
  {
    type: 'list',
    items: [
      'Your system will automatically connect to our VPN when hosting sessions',
      'All gaming traffic will be routed through our secure network infrastructure',
      'VPN connection is mandatory for licensing compliance and security',
      'Company may monitor and log VPN usage for compliance purposes',
    ],
  },
  { type: 'heading', level: 3, text: '3.2 Network Compliance' },
  { type: 'paragraph', text: 'The VPN requirement ensures:' },
  {
    type: 'list',
    items: [
      'Compliance with game publisher licensing terms',
      'Protection of intellectual property rights',
      'Secure data transmission between users',
      'Proper geographic licensing compliance',
      'Prevention of unauthorized game access',
    ],
  },
  { type: 'heading', level: 3, text: '3.3 Network Access Rights' },
  { type: 'paragraph', text: 'You grant Company the right to:' },
  {
    type: 'list',
    items: [
      'Route all gaming traffic through our VPN infrastructure',
      'Monitor network usage for compliance and security purposes',
      'Block or filter network traffic as necessary for legal compliance',
      'Implement geographic restrictions based on game licensing requirements',
    ],
  },

  // ── Addendum 4. Privacy and Data Implications ──
  { type: 'heading', level: 2, text: '4. Privacy and Data Implications of Distributed Gaming' },
  { type: 'heading', level: 3, text: '4.1 Third-Party Computer Data Exposure' },
  {
    type: 'paragraph',
    text: 'When playing games on other users\' computers through Playcast Pavilion™, you acknowledge that:',
  },
  {
    type: 'list',
    items: [
      'Your gaming behavior and inputs may be visible to the host computer owner',
      'Game data and save files may be stored on third-party systems',
      'Your IP address and network information may be exposed to host systems',
      'Host computer owners may have access to logs of your gaming activity',
    ],
  },
  { type: 'heading', level: 3, text: '4.2 Data Collection in Distributed Environment' },
  {
    type: 'paragraph',
    text: 'In addition to data collection described in our Privacy Policy, Playcast Pavilion™ may collect:',
  },
  {
    type: 'list',
    items: [
      'Cross-system performance metrics and compatibility data',
      'Distributed session quality and latency information',
      'Host-guest interaction patterns and usage analytics',
      'Network routing and VPN connection data',
    ],
  },
  { type: 'heading', level: 3, text: '4.3 Host Computer Data Exposure' },
  {
    type: 'paragraph',
    text: 'When hosting games for other users, you acknowledge that:',
  },
  {
    type: 'list',
    items: [
      'Guest user activities will be processed on your computer',
      'Game installation and configuration data will be created on your system',
      'Network traffic from other users will pass through your system',
      'Your system\'s IP address may be visible to Playcast\'s infrastructure',
    ],
  },
  { type: 'heading', level: 3, text: '4.4 Privacy Policy Application' },
  {
    type: 'paragraph',
    text: 'All data collection and usage practices are governed by our Privacy Policy at www.playcast.io, including data sharing with third-party analytics providers and international data transfers.',
  },

  // ── Addendum 5. Conditional Access Model ──
  { type: 'heading', level: 2, text: '5. Conditional Access Model' },
  { type: 'heading', level: 3, text: '5.1 Reciprocal Hosting Requirement' },
  {
    type: 'paragraph',
    text: 'Your access to games hosted on other users\' systems is conditioned upon your willingness to host games for other users. You understand that:',
  },
  {
    type: 'list',
    items: [
      'Game access is tied to active Playcast Pavilion™ participation',
      'Disabling hosting capabilities may result in loss of access to hosted games',
      'Company maintains the right to verify hosting participation',
      'Extended periods of non-participation may result in service suspension',
    ],
  },
  { type: 'heading', level: 3, text: '5.2 Idle Time Utilization' },
  {
    type: 'paragraph',
    text: 'You agree that during periods when your computer is idle:',
  },
  {
    type: 'list',
    items: [
      'Company may initiate gaming sessions for other users',
      'Your system resources may be fully utilized for hosting',
      'Gaming sessions may continue until you actively use your computer',
      'You will receive priority access to your own system when needed',
    ],
  },
  { type: 'heading', level: 3, text: '5.3 Access Revocation Rights' },
  { type: 'paragraph', text: 'Company reserves the absolute right to:' },
  {
    type: 'list',
    items: [
      'Immediately revoke your access to hosted games',
      'Suspend or terminate your Playcast Pavilion™ participation',
      'Remove installed games and services from your system',
      'Disable created accounts and associated data',
      'Take such action without prior notice or explanation',
    ],
  },

  // ── Addendum 6. User Conduct and Security Obligations ──
  { type: 'heading', level: 2, text: '6. User Conduct and Security Obligations' },
  { type: 'heading', level: 3, text: '6.1 General Conduct Requirements' },
  {
    type: 'paragraph',
    text: 'In addition to the specific prohibitions below, you must comply with all Prohibited Conduct provisions in our Terms of Service, which apply to all use of Playcast Pavilion™. This includes prohibitions against illegal activities, harassment, intellectual property violations, and system interference.',
  },
  { type: 'heading', level: 3, text: '6.2 Prohibited Malicious Activities' },
  {
    type: 'paragraph',
    text: 'You expressly agree NOT to engage in any of the following activities when other users are accessing games on your system:',
  },
  { type: 'heading', level: 4, text: '6.2.1 Data Theft and Privacy Violations' },
  {
    type: 'list',
    items: [
      'Attempting to capture, record, or access other users\' passwords',
      'Installing keyloggers or other monitoring software',
      'Accessing other users\' personal files or data',
      'Attempting to extract or copy user credentials',
      'Monitoring or recording user communications',
    ],
  },
  { type: 'heading', level: 4, text: '6.2.2 Input Interference and Manipulation' },
  {
    type: 'list',
    items: [
      'Interfering with other users\' game inputs or controls',
      'Injecting false inputs or commands',
      'Disrupting game sessions through input manipulation',
      'Altering or intercepting user commands',
      'Creating lag or delay in user inputs',
    ],
  },
  { type: 'heading', level: 4, text: '6.2.3 Unauthorized Communication and Messaging' },
  {
    type: 'list',
    items: [
      'Injecting unauthorized messages into game chat',
      'Sending unsolicited communications to players',
      'Impersonating other users or administrators',
      'Broadcasting unauthorized advertisements or content',
      'Interfering with legitimate game communications',
    ],
  },
  { type: 'heading', level: 4, text: '6.2.4 Session Disruption and Sabotage' },
  {
    type: 'list',
    items: [
      'Intentionally causing game crashes or failures',
      'Modifying game files during active sessions',
      'Disrupting network connections for hosted sessions',
      'Installing unauthorized modifications or cheats',
      'Degrading game performance intentionally',
    ],
  },
  { type: 'heading', level: 3, text: '6.3 Security Monitoring' },
  { type: 'paragraph', text: 'You acknowledge that Company may:' },
  {
    type: 'list',
    items: [
      'Monitor your system for malicious activity',
      'Log user interactions during hosted sessions',
      'Implement security measures to protect other users',
      'Automatically detect and prevent prohibited activities',
      'Share security incident information with law enforcement',
    ],
  },
  { type: 'heading', level: 3, text: '6.4 Immediate Termination for Violations' },
  { type: 'paragraph', text: 'Any violation of Section 6.2 will result in:' },
  {
    type: 'list',
    items: [
      'Immediate termination of your Playcast Pavilion™ access',
      'Removal of all Company software from your system',
      'Permanent ban from all Company services',
      'Potential legal action for damages caused',
      'Reporting of criminal activity to appropriate authorities',
    ],
  },

  // ── Addendum 7. Enhanced Liability and Indemnification ──
  { type: 'heading', level: 2, text: '7. Enhanced Liability and Indemnification' },
  { type: 'heading', level: 3, text: '7.1 User Liability for Guest Sessions' },
  { type: 'paragraph', text: 'You are fully liable for:' },
  {
    type: 'list',
    items: [
      'Any damage caused to other users\' gaming experiences',
      'Security breaches originating from your system',
      'Malicious activities conducted from your computer',
      'Unauthorized access to other users\' data or accounts',
      'Any violations of game publishers\' terms of service',
    ],
  },
  { type: 'heading', level: 3, text: '7.2 Enhanced Liability for Distributed Gaming Risks' },
  {
    type: 'paragraph',
    text: 'You specifically acknowledge and accept liability for:',
  },
  {
    type: 'list',
    items: [
      'Any security vulnerabilities on your system that may affect guest users',
      'Privacy breaches that may occur when hosting games for others',
      'System instability that may impact other users\' gaming experiences',
      'Data loss or corruption affecting guest users\' game progress',
      'Any malicious software or vulnerabilities present on your computer',
    ],
  },
  { type: 'heading', level: 3, text: '7.3 Expanded Indemnification' },
  {
    type: 'paragraph',
    text: 'In addition to Main EULA indemnification, you agree to indemnify Company against:',
  },
  {
    type: 'list',
    items: [
      'Claims from other users regarding privacy violations',
      'Damages from malicious activities on your system',
      'Legal costs from game publisher licensing violations',
      'Security incidents affecting other network users',
      'Any regulatory violations related to your system\'s use',
      'Claims arising from third-party computer usage through Playcast Pavilion™',
    ],
  },
  { type: 'heading', level: 3, text: '7.4 Third-Party User Protection' },
  {
    type: 'paragraph',
    text: 'You acknowledge your responsibility to protect other users and agree to:',
  },
  {
    type: 'list',
    items: [
      'Maintain system security during hosted sessions',
      'Report any suspicious activity immediately',
      'Cooperate with security investigations',
      'Implement reasonable security measures on your system',
    ],
  },

  // ── Addendum 8. Technical Requirements and Limitations ──
  { type: 'heading', level: 2, text: '8. Technical Requirements and Limitations' },
  { type: 'heading', level: 3, text: '8.1 System Requirements' },
  { type: 'paragraph', text: 'Playcast Pavilion™ requires:' },
  {
    type: 'list',
    items: [
      'Stable internet connection with minimum upload/download speeds',
      'Sufficient system resources to host multiple game sessions',
      'Compatible operating system and hardware configuration',
      'Administrative privileges for account and service management',
    ],
  },
  { type: 'heading', level: 3, text: '8.2 Performance Expectations' },
  { type: 'paragraph', text: 'You acknowledge that:' },
  {
    type: 'list',
    items: [
      'Hosting sessions may significantly impact your system performance',
      'Game installations may consume substantial storage space',
      'Network bandwidth may be fully utilized during peak periods',
      'System temperature and power consumption may increase',
    ],
  },
  { type: 'heading', level: 3, text: '8.3 Compatibility Disclaimer' },
  { type: 'paragraph', text: 'Company does not guarantee:' },
  {
    type: 'list',
    items: [
      'Compatibility with all games or gaming platforms',
      'Optimal performance on all hardware configurations',
      'Uninterrupted service availability',
      'Success of all hosted gaming sessions',
    ],
  },

  // ── Addendum 9. Data Handling and Game Content ──
  { type: 'heading', level: 2, text: '9. Data Handling and Game Content' },
  { type: 'heading', level: 3, text: '9.1 Game Content Management' },
  { type: 'paragraph', text: 'Company may:' },
  {
    type: 'list',
    items: [
      'Download and install games from various digital platforms',
      'Manage game licenses and authentication',
      'Store game save data and user preferences',
      'Synchronize game content across the network',
    ],
  },
  { type: 'heading', level: 3, text: '9.2 User Data Separation' },
  { type: 'paragraph', text: 'You understand that:' },
  {
    type: 'list',
    items: [
      'Multiple user profiles may exist on your system simultaneously',
      'Game data for different users will be kept separate',
      'Company implements measures to prevent data cross-contamination',
      'You should not attempt to access other users\' game data',
    ],
  },

  // ── Addendum 10. Pavilion-Specific Termination ──
  { type: 'heading', level: 2, text: '10. Playcast Pavilion™-Specific Termination' },
  { type: 'heading', level: 3, text: '10.1 Playcast Pavilion™ Termination' },
  { type: 'paragraph', text: 'Company may terminate Playcast Pavilion™ services:' },
  {
    type: 'list',
    items: [
      'For any violation of this Addendum',
      'For extended periods of non-participation',
      'For technical incompatibility issues',
      'At Company\'s sole discretion without cause',
    ],
  },
  { type: 'heading', level: 3, text: '10.2 Effect of Playcast Pavilion™ Termination' },
  { type: 'paragraph', text: 'Upon termination:' },
  {
    type: 'list',
    items: [
      'All created accounts will be removed from your system',
      'Installed games will be uninstalled',
      'VPN access will be immediately revoked',
      'Your access to hosted games will cease',
      'Main EULA terms remain in effect for base software',
    ],
  },

  // ── Addendum 11. Dispute Resolution and Governing Law ──
  { type: 'heading', level: 2, text: '11. Dispute Resolution and Governing Law' },
  { type: 'heading', level: 3, text: '11.1 Arbitration' },
  {
    type: 'paragraph',
    text: 'Any disputes arising from this Addendum or use of Playcast Pavilion™ are subject to the binding arbitration provisions in our Terms of Service, including the class action waiver. You agree to resolve disputes through individual arbitration as specified in our Terms of Service.',
  },
  { type: 'heading', level: 3, text: '11.2 Governing Law' },
  {
    type: 'paragraph',
    text: 'This Addendum is governed by the laws of the State of Delaware, consistent with our Terms of Service.',
  },

  // ── Addendum 12. Regulatory Compliance ──
  { type: 'heading', level: 2, text: '12. Regulatory Compliance' },
  { type: 'heading', level: 3, text: '12.1 Gaming License Compliance' },
  { type: 'paragraph', text: 'You acknowledge that:' },
  {
    type: 'list',
    items: [
      'All game access is subject to publisher licensing terms',
      'Geographic restrictions may apply to certain games',
      'Company\'s VPN infrastructure ensures license compliance',
      'Violations may result in legal action by game publishers',
    ],
  },
  { type: 'heading', level: 3, text: '12.2 International Considerations' },
  { type: 'paragraph', text: 'You agree to comply with:' },
  {
    type: 'list',
    items: [
      'Local laws regarding distributed computing services',
      'International copyright and licensing regulations',
      'Export control laws for software and technology',
      'Privacy laws affecting cross-border data transfer',
    ],
  },

  // ── Addendum 13. Acknowledgment ──
  { type: 'heading', level: 2, text: '13. Acknowledgment of Playcast Pavilion™ Terms' },
  {
    type: 'paragraph',
    text: 'By enabling Playcast Pavilion™ functionality, you acknowledge that you have read, understood, and agree to be bound by all terms in this Addendum in addition to the Main EULA and our Terms of Service. You understand the significant system access and resource sharing involved and consent to all described activities.',
  },
  {
    type: 'paragraph',
    text: 'CRITICAL ACKNOWLEDGMENT: You specifically acknowledge that you understand the risks associated with distributed gaming, including playing games on other users\' computers and hosting games for other users, and that Playcast disclaims responsibility for security, privacy, or other issues that may arise from this distributed model.',
  },
  { type: 'heading', level: 3, text: 'Contact Information' },
  {
    type: 'list',
    items: [
      'General support: support@playcast.io',
      'Privacy inquiries: privacy@playcast.io',
      'Address: 1000 SE Everett Mall Way #40p, Everett, WA 98208',
    ],
  },
  {
    type: 'paragraph',
    text: 'Playcast Pavilion™ Addendum Version: 1.0',
  },
  {
    type: 'paragraph',
    text: 'Last Updated: June 11th, 2025',
  },
  {
    type: 'paragraph',
    text: 'TO ENABLE PLAYCAST PAVILION™: You must explicitly accept these additional terms by checking the "I agree to Playcast Pavilion™ Addendum terms" box during software installation or in the application settings.',
  },
];
